import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { fetchTenants } from '../store/slices/tenantSlice';
import { fetchClusterConfigs } from '../store/slices/configSlice';
import { fetchEmailDomains } from '../store/slices/domainSlice';
import { Link } from 'react-router-dom';
import { FiPlus, FiUsers, FiServer, FiGlobe, FiActivity } from 'react-icons/fi';

const DashboardPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { tenants } = useSelector((state: RootState) => state.tenants);
    const { clusters } = useSelector((state: RootState) => state.clusters);
    const { emailDomains } = useSelector((state: RootState) => state.domains);

    // Separate loading states for different data sources
    const [tenantsLoading, setTenantsLoading] = useState(true);
    const [clustersLoading, setClustersLoading] = useState(true);
    const [domainsLoading, setDomainsLoading] = useState(true);
    const [pageLoading, setPageLoading] = useState(true);

    // Calculated stats
    const [tenantStats, setTenantStats] = useState({
        total: 0,
        active: 0,
        activePercentage: 0
    });

    const [clusterStats, setClusterStats] = useState({
        total: 0,
        active: 0,
        activePercentage: 0
    });

    // Fetch tenants data
    useEffect(() => {
        const loadTenants = async () => {
            setTenantsLoading(true);
            try {
                await dispatch(fetchTenants());
            } finally {
                setTenantsLoading(false);
            }
        };
        loadTenants();
    }, [dispatch]);

    // Fetch clusters data
    useEffect(() => {
        const loadClusters = async () => {
            setClustersLoading(true);
            try {
                await dispatch(fetchClusterConfigs());
            } finally {
                setClustersLoading(false);
            }
        };
        loadClusters();
    }, [dispatch]);

    // Fetch domains data
    useEffect(() => {
        const loadDomains = async () => {
            setDomainsLoading(true);
            try {
                /*await Promise.all([
                    dispatch(fetchEmailDomains()),
                    dispatch(fetchAppServices())
                ]);*/
            } finally {
                setDomainsLoading(false);
            }
        };
        loadDomains();
    }, [dispatch]);

    // Track overall page loading state
    useEffect(() => {
        setPageLoading(tenantsLoading || clustersLoading || domainsLoading);
    }, [tenantsLoading, clustersLoading, domainsLoading]);

    // Calculate tenant stats whenever tenant data changes
    useEffect(() => {
        if (!tenantsLoading && tenants.length >= 0) {
            // Count tenants by status
            const tenantStatusCounts = tenants.reduce<Record<string, number>>((counts, tenant) => {
                // Only count non-deleted tenants
                if (tenant.status !== 'deleted') {
                    const status = tenant.status || 'unknown';
                    counts[status] = (counts[status] || 0) + 1;
                }
                return counts;
            }, {});

            // Calculate stats
            const activeTenants = tenantStatusCounts['active'] || 0;
            const totalTenants = tenants.filter(t => t.status !== 'deleted').length;
            const activePercentage = totalTenants > 0 ? Math.round((activeTenants / totalTenants) * 100) : 0;

            setTenantStats({
                total: totalTenants,
                active: activeTenants,
                activePercentage
            });
        }
    }, [tenants, tenantsLoading]);

    // Calculate cluster stats whenever cluster data changes
    useEffect(() => {
        if (!clustersLoading && clusters.length >= 0) {
            // Count active clusters vs total clusters
            const activeClusters = clusters.filter(cluster => cluster.status === 'active').length;
            const totalClusters = clusters.length;
            const activePercentage = totalClusters > 0 ? Math.round((activeClusters / totalClusters) * 100) : 0;

            setClusterStats({
                total: totalClusters,
                active: activeClusters,
                activePercentage
            });
        }
    }, [clusters, clustersLoading]);

    // Loading indicator component
    const LoadingIndicator = () => (
        <div className="flex justify-center items-center h-12">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-500"></div>
        </div>
    );

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
                <Link
                    to="/tenants/create"
                    className="mt-3 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                    <FiPlus className="mr-2" />
                    New Tenant
                </Link>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {/* Total Tenants Card */}
                <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <FiUsers className="h-6 w-6 text-gray-400" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                        Total Tenants
                                    </dt>
                                    <dd>
                                        <div className="text-lg font-medium text-gray-900 dark:text-white">
                                            {tenantsLoading ? <LoadingIndicator /> : tenantStats.total}
                                        </div>
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 px-5 py-3">
                        <div className="text-sm">
                            <Link
                                to="/tenants"
                                className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500"
                            >
                                View all
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Active Tenants Card */}
                <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <FiActivity className="h-6 w-6 text-gray-400" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                        Active Tenants
                                    </dt>
                                    <dd>
                                        <div className="text-lg font-medium text-gray-900 dark:text-white">
                                            {tenantsLoading ? (
                                                <LoadingIndicator />
                                            ) : (
                                                `${tenantStats.active} (${tenantStats.activePercentage}%)`
                                            )}
                                        </div>
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 px-5 py-3">
                        <div className="relative pt-1">
                            {tenantsLoading ? (
                                <div className="h-2 rounded bg-gray-200 dark:bg-gray-600 animate-pulse"></div>
                            ) : (
                                <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200 dark:bg-gray-600">
                                    <div
                                        style={{ width: `${tenantStats.activePercentage}%` }}
                                        className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-green-500"
                                    ></div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Total Clusters Card */}
                <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <FiServer className="h-6 w-6 text-gray-400" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                        Total Clusters
                                    </dt>
                                    <dd>
                                        <div className="text-lg font-medium text-gray-900 dark:text-white">
                                            {clustersLoading ? <LoadingIndicator /> : clusterStats.total}
                                        </div>
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 px-5 py-3">
                        <div className="text-sm">
                            <Link
                                to="/clusters"
                                className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500"
                            >
                                Manage clusters
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Active Clusters Card */}
                <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <FiServer className="h-6 w-6 text-gray-400" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                                        Active Clusters
                                    </dt>
                                    <dd>
                                        <div className="text-lg font-medium text-gray-900 dark:text-white">
                                            {clustersLoading ? (
                                                <LoadingIndicator />
                                            ) : (
                                                `${clusterStats.active} (${clusterStats.activePercentage}%)`
                                            )}
                                        </div>
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 px-5 py-3">
                        <div className="relative pt-1">
                            {clustersLoading ? (
                                <div className="h-2 rounded bg-gray-200 dark:bg-gray-600 animate-pulse"></div>
                            ) : (
                                <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200 dark:bg-gray-600">
                                    <div
                                        style={{ width: `${clusterStats.activePercentage}%` }}
                                        className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"
                                    ></div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Tenants */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                <div className="px-4 py-5 sm:px-6 border-b dark:border-gray-700">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Recent Tenants</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Latest tenants added to the system
                    </p>
                </div>

                {pageLoading ? (
                    <div className="flex justify-center p-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                        >
                                            Name
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                        >
                                            Domain
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                        >
                                            Status
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                        >
                                            Cluster
                                        </th>
                                        <th scope="col" className="relative px-6 py-3">
                                            <span className="sr-only">Edit</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {tenants.slice(0, 5).map((tenant) => {
                                        const clusterName = clusters.find(c => c.clusterId === tenant.cluster)?.name || tenant.cluster;

                                        return (
                                            <tr key={tenant._id}>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">{tenant.name}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-500 dark:text-gray-400">{tenant.baseUrl}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span
                                                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${tenant.status === 'active'
                                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                                                : tenant.status === 'suspended'
                                                                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                                                    : tenant.status === 'provisioning'
                                                                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                                                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                                                            }`}
                                                    >
                                                        {tenant.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                    {clusterName}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <Link
                                                        to={`/tenants/${tenant._id}`}
                                                        className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                                                    >
                                                        View
                                                    </Link>
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    {tenants.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                                No tenants found. Create your first tenant to get started.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {tenants.length > 5 && (
                            <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-t dark:border-gray-700">
                                <Link
                                    to="/tenants"
                                    className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500"
                                >
                                    View all tenants
                                </Link>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default DashboardPage;